import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import type {
  Address,
  Invoice,
  InvoiceLine,
  InvoiceStatus,
  Money,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { EmptyState, LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDate, formatMoney } from "@/modules/gst/format";

/**
 * A tax invoice, read-only, as `screens/InvoiceList.tsx` shows it in place
 * of the list when a row is clicked.
 *
 * There is no "GST > Invoice detail" section in `config/modules.ts` — the
 * navigation map only names `invoices`, `credit-notes`, `hsn-summary` and
 * `exports` — so this is not a routed screen in its own right. It is reached
 * from within the Invoices list, which is also why it takes its id and a
 * way back as plain props instead of reading a route param.
 *
 * Read-only regardless of status, not only for `issued`. FR-GST-15 makes an
 * issued invoice immutable — correction is by credit note, never by editing
 * — and generating or issuing a draft is a write flow that belongs to a
 * different ticket, not this one. So there is no edit affordance here at
 * all, for any status.
 */
export function InvoiceDetail({
  invoiceId,
  onBack,
}: {
  invoiceId: string;
  onBack?: () => void;
}) {
  const data = useDataSource();

  const invoiceQuery = useQuery({
    queryKey: ["gst", "invoice", invoiceId],
    queryFn: () => data.invoicing.get(invoiceId),
  });

  const orderId = invoiceQuery.data?.orderId;

  /**
   * Fetched through the same `DataSource` the rest of the app uses, not a
   * join the fixture happens to offer — `orderId` is all `Invoice` carries,
   * and printing that raw id in place of "the order it came from" would
   * mean nothing to whoever is reading this document.
   */
  const orderQuery = useQuery({
    queryKey: ["gst", "invoice-order", orderId],
    queryFn: () => {
      if (orderId === undefined) return Promise.resolve(null);
      return data.orders.get(orderId);
    },
    enabled: orderId !== undefined,
  });

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      {/*
        Optional: the drawer this renders inside carries its own close, and two
        ways out of one panel side by side each look like they do something
        different.
      */}
      {onBack ? (
        <div className="shrink-0">
          <Button size="sm" variant="ghost" onClick={onBack}>
            <ArrowLeft />
            Back to invoices
          </Button>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto pb-8">
        {invoiceQuery.isPending ? (
          <TableSkeleton />
        ) : invoiceQuery.isError ? (
          <LoadFailed
            message={invoiceQuery.error.message}
            onRetry={() => {
              void invoiceQuery.refetch();
            }}
          />
        ) : invoiceQuery.data === null ? (
          <EmptyState
            title="Invoice not found"
            description="It may have been removed since the list was loaded."
          />
        ) : (
          <InvoiceDocument
            invoice={invoiceQuery.data}
            orderNumber={orderQuery.data?.orderNumber ?? null}
          />
        )}
      </div>
    </div>
  );
}

function InvoiceDocument({
  invoice,
  orderNumber,
}: {
  invoice: Invoice;
  orderNumber: string | null;
}) {
  // The one branch this whole document turns on. `taxKind` was decided once,
  // by comparing the order's shipping state against the seller's registered
  // state at generation time (FR-GST-4), and is stored rather than
  // recomputed here — so this reads it, it does not re-derive it.
  const isCgstSgst = invoice.taxKind === "cgst_sgst";

  return (
    <div className="bg-card mx-auto max-w-3xl space-y-6 rounded-lg border p-8 text-sm">
      <div className="flex items-start justify-between gap-6 border-b pb-4">
        <div className="space-y-1">
          <p className="text-lg font-semibold tracking-wide">TAX INVOICE</p>
          <StatusBadge status={invoice.status} />
        </div>
        <div className="text-right">
          <p className="font-medium">
            {invoice.invoiceNumber ?? "Not yet issued"}
          </p>
          <p className="text-muted-foreground">
            {invoice.invoiceDate ? formatDate(invoice.invoiceDate) : "—"}
          </p>
          {orderNumber !== null ? (
            <p className="text-muted-foreground">Order {orderNumber}</p>
          ) : null}
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <PartyBlock
          heading="Seller"
          name={invoice.sellerLegalName}
          gstin={invoice.sellerGstin}
          address={invoice.sellerAddress}
        />
        <PartyBlock
          heading="Buyer"
          name={invoice.buyerName}
          gstin={invoice.buyerGstin}
          address={invoice.buyerAddress}
        />
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Fact label="Place of supply" value={invoice.placeOfSupplyStateCode} />
        <Fact label="Tax type" value={isCgstSgst ? "CGST + SGST" : "IGST"} />
        <Fact
          label="Prices"
          value={
            invoice.pricesAreTaxInclusive ? "Tax inclusive" : "Tax exclusive"
          }
        />
      </div>

      <LinesTable lines={invoice.lines} isCgstSgst={isCgstSgst} />

      <TotalsBlock invoice={invoice} isCgstSgst={isCgstSgst} />
    </div>
  );
}

function PartyBlock({
  heading,
  name,
  gstin,
  address,
}: {
  heading: string;
  name: string;
  gstin: string | null;
  address: Address | null;
}) {
  return (
    <div>
      <p className="text-muted-foreground text-xs font-medium uppercase">
        {heading}
      </p>
      <p className="font-medium">{name}</p>
      {address ? <AddressLines address={address} /> : null}
      <p className="text-muted-foreground">
        GSTIN: {gstin ?? "Not registered"}
      </p>
    </div>
  );
}

function AddressLines({ address }: { address: Address }) {
  const lines: string[] = [
    address.line1,
    address.line2,
    [address.city, address.province].filter(Boolean).join(", "),
    [address.zip, address.country].filter(Boolean).join(" "),
  ].filter((line): line is string => Boolean(line));

  if (lines.length === 0) return null;

  return (
    <p className="text-muted-foreground whitespace-pre-line">
      {lines.join("\n")}
    </p>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-muted-foreground text-xs uppercase">{label}</p>
      <p>{value}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: InvoiceStatus }) {
  if (status === "issued") return <Badge>Issued</Badge>;
  if (status === "cancelled") {
    return <Badge variant="destructive">Cancelled</Badge>;
  }
  return <Badge variant="secondary">Draft</Badge>;
}

/**
 * Line items, with the CGST/SGST-versus-IGST split rendered as two entirely
 * different sets of columns rather than one set with a value zeroed out.
 *
 * `taxKind` is per-invoice, not per-line, so a CGST/SGST invoice never has
 * an IGST line and vice versa — but the columns are chosen once here from
 * `isCgstSgst` for exactly the reason the ticket calls out: a filed return
 * built off a screen that shows both, with the wrong one at zero, reads as
 * data entry rather than as "this document only ever had one of these."
 */
function LinesTable({
  lines,
  isCgstSgst,
}: {
  lines: InvoiceLine[];
  isCgstSgst: boolean;
}) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full border-collapse text-sm">
        <thead className="bg-muted/40">
          <tr className="border-b text-left">
            <th className="px-3 py-2 font-medium">Description</th>
            <th className="px-3 py-2 font-medium">HSN</th>
            <th className="px-3 py-2 text-right font-medium">Qty</th>
            <th className="px-3 py-2 text-right font-medium">Unit price</th>
            <th className="px-3 py-2 text-right font-medium">Taxable value</th>
            <th className="px-3 py-2 text-right font-medium">Rate</th>
            {isCgstSgst ? (
              <>
                <th className="px-3 py-2 text-right font-medium">CGST</th>
                <th className="px-3 py-2 text-right font-medium">SGST</th>
              </>
            ) : (
              <th className="px-3 py-2 text-right font-medium">IGST</th>
            )}
            <th className="px-3 py-2 text-right font-medium">Line total</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.id} className="border-b last:border-b-0">
              <td className="px-3 py-1.5">{line.description}</td>
              <td className="px-3 py-1.5 tabular-nums">{line.hsnCode}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                {line.quantity}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                {formatMoney(line.unitPrice)}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                {formatMoney(line.taxableValue)}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                {line.gstRate}%
              </td>
              {isCgstSgst ? (
                <>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {formatMoney(line.cgstAmount)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {formatMoney(line.sgstAmount)}
                  </td>
                </>
              ) : (
                <td className="px-3 py-1.5 text-right tabular-nums">
                  {formatMoney(line.igstAmount)}
                </td>
              )}
              <td className="px-3 py-1.5 text-right font-medium tabular-nums">
                {formatMoney(line.lineTotal)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TotalsBlock({
  invoice,
  isCgstSgst,
}: {
  invoice: Invoice;
  isCgstSgst: boolean;
}) {
  return (
    <div className="ml-auto flex w-full max-w-xs flex-col gap-1 sm:max-w-sm">
      <TotalRow label="Taxable value" value={invoice.totalTaxableValue} />
      {isCgstSgst ? (
        <>
          <TotalRow label="CGST" value={invoice.totalCgst} />
          <TotalRow label="SGST" value={invoice.totalSgst} />
        </>
      ) : (
        <TotalRow label="IGST" value={invoice.totalIgst} />
      )}
      {/* A check against zero to decide whether to print the row at all —
          not arithmetic that feeds an output value, so it does not run
          into the reason `Money` stays a string everywhere else here. */}
      {Number(invoice.totalCess) !== 0 ? (
        <TotalRow label="Cess" value={invoice.totalCess} />
      ) : null}
      <TotalRow label="Round off" value={invoice.roundOff} />
      <TotalRow label="Total" value={invoice.total} emphasis />
    </div>
  );
}

function TotalRow({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: Money;
  emphasis?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex justify-between gap-4 tabular-nums",
        emphasis && "border-t pt-1 text-base font-semibold",
      )}
    >
      <span className={emphasis ? "" : "text-muted-foreground"}>{label}</span>
      <span>{formatMoney(value)}</span>
    </div>
  );
}
